import React, { useState, useEffect } from "react";
import { SOD_APIURL } from "variables/constants";

// Components
import Card from "components/card";
import {
  SODStatsCard,
  SODActivityChart,
  SODDistributionChart,
  SODTypePieChart
} from "components/sod";
import LineChart from "components/charts/LineChart";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
// Add these imports for the floating reset button
import { Fab } from 'react-tiny-fab';
import { IoCloseSharp } from "react-icons/io5";
import 'react-tiny-fab/dist/styles.css';

// Icons
import KanbanIcon from "components/icons/KanbanIcon";
import MarketIcon from "components/icons/MarketIcon";
import TablesIcon from "components/icons/TablesIcon";
import ProfileIcon from "components/icons/ProfileIcon";
import NotificationIcon from "components/icons/NotificationIcon";
import ThemsIcon from "components/icons/ThemsIcon";
import VideoIcon from "components/icons/VideoIcon";
import ClockIcon1 from "components/icons/ClockIcon1";
import DollarIcon from "components/icons/WidgetIcon/DollarIcon";
import SearchIcon from "components/icons/SearchIcon";

const SODDashboard = () => {
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [editStats, setEditStats] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(0);
  const [referencePopupUrl, setReferencePopupUrl] = useState(null);
  const [logDetailsPopup, setLogDetailsPopup] = useState(null);
  const [viewingUrlFromLogDetails, setViewingUrlFromLogDetails] = useState(false);
  const LOGS_PER_PAGE = 20;
  const [userIdToName, setUserIdToName] = useState({});
  const EMPLOYEE_NAMES = ["Derek", "Rodi", "John", "Toni", "Jessica", "Ella", "Zahi"];
  const [aiSODs, setAiSODs] = useState([]);
  const [aiSODDetails, setAiSODDetails] = useState(null);
  const [itemTypeStats, setItemTypeStats] = useState([]);
  const [metalTypeStats, setMetalTypeStats] = useState([]);
  const [showTotalCost, setShowTotalCost] = useState(false);
  const [logVideos, setLogVideos] = useState({});
  const fetchedVideoIds = React.useRef(new Set());

  // Date range filter state
  const [dateRange, setDateRange] = useState([null, null]);
  
  // ID search filter state
  const [idSearchTerm, setIdSearchTerm] = useState("");
  
  // Customer filter state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const customerDropdownRef = React.useRef(null);
  // Retailer filter state
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [retailerSearchTerm, setRetailerSearchTerm] = useState("");
  const [showRetailerDropdown, setShowRetailerDropdown] = useState(false);
  const retailerDropdownRef = React.useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setShowCustomerDropdown(false);
      }
      if (retailerDropdownRef.current && !retailerDropdownRef.current.contains(event.target)) {
        setShowRetailerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fetchSODData();
  }, []);

  // Fetch user info for mapping IDs to emails after userStats are loaded
  useEffect(() => {
    // Collect all possible user IDs from userStats and logs
    const userStatIds = (userStats || []).map(u => u.user_id || u.userID || u.id || u.userid || u.email).filter(Boolean);
    const logIds = (logs || []).map(l => l.user_id || l.userID || l.id || l.userid || l.email).filter(Boolean);
    // Combine and deduplicate
    const allIds = Array.from(new Set([...userStatIds, ...logIds]));
    if (allIds.length === 0) return;
    // Debug log
    console.log('Fetching user info for IDs:', allIds);
    // Fetch in batches of 50 to avoid URL length issues
    const fetchUserInfo = async () => {
      let idToName = {};
      for (let i = 0; i < allIds.length; i += 50) {
        const batch = allIds.slice(i, i + 50);
        try {
          const res = await fetch(`https://api.gabrielny.com:8080/ai_dashboard.php?query=searchUser&userid=${batch.join(",")}`);
          const data = await res.json();
          // data is expected to be an array of user info objects
          if (Array.isArray(data)) {
            data.forEach(user => {
              if (user && user.userId) {
                idToName[user.userId] = { userId: user.userId, userEmail: user.userEmail || null };
              }
            });
          }
        } catch (e) { /* ignore errors for now */ }
      }
      // Debug log
      console.log('userIdToName mapping:', idToName);
      setUserIdToName(idToName);
    };
    fetchUserInfo();
  }, [userStats, logs]);

  const fetchSODData = async () => {
    try {
      setLoading(true);

      // Fetch all relevant SOD endpoints
      const [statsRes, allRes, perdayRes, editStatsRes, userStatsRes, aiSODsRes, itemTypeStatsRes, metalTypeStatsRes] = await Promise.all([
        fetch(`${SOD_APIURL}?query=sod_stats`),
        fetch(`${SOD_APIURL}?query=sod_all`),
        fetch(`${SOD_APIURL}?query=sod_perday`),
        fetch(`${SOD_APIURL}?query=sod_editStats`),
        fetch(`${SOD_APIURL}?query=sod_userStats`),
        fetch(`${SOD_APIURL}?query=getaisods`),
        fetch(`${SOD_APIURL}?query=sod_itemTypeStats`),
        fetch(`${SOD_APIURL}?query=sod_metalTypeStats`)
      ]);

      const [statsData, allData, perdayData, editStatsData, userStatsData, aiSODsData, itemTypeStatsData, metalTypeStatsData] = await Promise.all([
        statsRes.json(),
        allRes.json(),
        perdayRes.json(),
        editStatsRes.json(),
        userStatsRes.json(),
        aiSODsRes.json(),
        itemTypeStatsRes.json(),
        metalTypeStatsRes.json()
      ]);

      // Debug logging for duplicates
      console.log('Raw allData length:', allData.length);
      console.log('Raw allData sample:', allData.slice(0, 3));

      // Deduplicate the logs
      const deduplicatedLogs = deduplicateLogs(allData);
      console.log('Deduplicated logs length:', deduplicatedLogs.length);
      console.log('Removed duplicates:', allData.length - deduplicatedLogs.length);

      setStats(statsData);
      setLogs(deduplicatedLogs);
      setDailyData(perdayData);
      setEditStats(editStatsData);
      setUserStats(userStatsData);
      setAiSODs(Array.isArray(aiSODsData) ? aiSODsData : []);
      setItemTypeStats(itemTypeStatsData);
      setMetalTypeStats(metalTypeStatsData);

    } catch (error) {
      console.error("Error fetching SOD data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Deduplication function to remove duplicate logs
  const deduplicateLogs = (logs) => {
    if (!Array.isArray(logs)) return [];
    
    const seen = new Map();
    const deduplicated = [];
    
    logs.forEach(log => {
      // Create a unique key based on key fields that should be the same for duplicates
      const key = createLogKey(log);
      
      if (!seen.has(key)) {
        seen.set(key, true);
        deduplicated.push(log);
      } else {
        console.log('Removing duplicate log:', {
          id: log.id,
          user_id: log.user_id,
          datetime: log.datetime,
          info: log.info?.substring(0, 50) + '...'
        });
      }
    });
    
    return deduplicated;
  };

  // Create a unique key for deduplication based on key fields
  const createLogKey = (log) => {
    // Use fields that should be identical for duplicates
    const keyFields = [
      log.user_id,
      log.datetime,
      log.info,
      log.generated_prompt,
      log.references_url,
      log.generated_image
    ];
    
    // Create a string key from these fields
    return keyFields.map(field => String(field || '')).join('|');
  };

  // Update a SOD record
  const updateSODRecord = async (recordId, updateData) => {
    try {
      const response = await fetch(`${SOD_APIURL}?query=sod_updateRecord`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: recordId, ...updateData }),
      });
      if (!response.ok) throw new Error('Failed to update record');
      return await response.json();
    } catch (error) {
      console.error('Error updating SOD record:', error);
      throw error;
    }
  };

  const EXCLUDED_USERS = [
    "523617",
    "34613",
    "dhutchinson@gabrielny.com",
    "34583",
    "unknown",
    "tony.wehbe@gabrielny.com"
  ];

  // Helper to check if a user should be excluded
  const isExcludedUser = (userIdOrEmail) => {
    if (!userIdOrEmail) return false;
    const idStr = String(userIdOrEmail).toLowerCase();
    return EXCLUDED_USERS.some(ex => idStr === String(ex).toLowerCase());
  };

  // Filter out test submissions and excluded users from AI SOD Requests
  const filteredAiSODs = aiSODs.filter(ai => {
    const fieldsToCheck = [
      ai.designNo,
      ai.specialinstructions,
      ai.customerPo,
      ai.status,
      ai.createpersonName,
      ai.customerEmail
    ];
    // Exclude test and excluded users
    const isTest = fieldsToCheck.some(
        field => typeof field === 'string' && field.toLowerCase().includes('test')
    );
    const isExcluded = isExcludedUser(ai.createpersonName) || isExcludedUser(ai.customerEmail);
    return !isTest && !isExcluded;
  });

  // Filter userStats for excluded users
  const filteredUserStats = (userStats || []).filter(user => {
    const userId = user.user_id || user.userID || user.id || user.userid || user.email;
    const email = userIdToName[userId]?.userEmail || null;
    return !isExcludedUser(userId) && (!email || !isExcludedUser(email));
  });

  // Filter logs for excluded users and remove any test case
  const filteredLogs = (logs || []).filter(log => {
    const userId = log.user_id || log.userID || log.id || log.userid || log.email;
    const email = userIdToName[userId]?.userEmail || null;
    if (isExcludedUser(userId) || (email && isExcludedUser(email))) return false;
    // Exclude if any string field contains 'test'
    for (const key in log) {
      if (typeof log[key] === 'string' && log[key].toLowerCase().includes('test')) {
        return false;
      }
    }
    return true;
  });

  // Filter dailyData for excluded users (if dailyData contains user info)
  // If dailyData is per day only, skip this. If it contains user breakdown, filter accordingly.
  // We'll assume dailyData is an array of {date, record_count, edits_count, no_edits_count, user_id?}
  let filteredDailyData = dailyData;
  if (dailyData && dailyData.length > 0 && dailyData[0].user_id !== undefined) {
    filteredDailyData = dailyData.filter(d => !isExcludedUser(d.user_id));
  }

  // Filter editStats for excluded users (if editStats contains user info)
  let filteredEditStats = editStats;
  if (editStats && editStats.length > 0 && editStats[0].user_id !== undefined) {
    filteredEditStats = editStats.filter(e => !isExcludedUser(e.user_id));
  }

  // Helper to check if a date is within the selected range
  const isWithinDateRange = (dateStr) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return true;
    const date = new Date(dateStr);
    const start = new Date(dateRange[0]);
    const end = new Date(dateRange[1]);
    return date >= start && date <= end;
  };

  // Extract unique retailers from all customers (domain after @ in email)
  const extractRetailers = () => {
    const retailers = new Set();
    // From logs
    filteredLogs.forEach(log => {
      const nameObj = userIdToName[log.user_id];
      const email = nameObj && nameObj.userEmail ? nameObj.userEmail : null;
      if (email && email.includes("@")) {
        const domain = email.split("@")[1].toLowerCase();
        if (domain && !isExcludedUser(email)) retailers.add(domain);
      }
    });
    // From AI SODs
    filteredAiSODs.forEach(ai => {
      const email = ai.customerEmail;
      if (email && email.includes("@")) {
        const domain = email.split("@")[1].toLowerCase();
        if (domain && !isExcludedUser(email)) retailers.add(domain);
      }
    });
    return Array.from(retailers).sort();
  };
  const allRetailers = extractRetailers();
  const filteredRetailers = allRetailers.filter(retailer =>
    retailer.toLowerCase().includes(retailerSearchTerm.toLowerCase())
  );

  // Extract unique customers from logs and AI SODs, filtered by selectedRetailer if set
  const extractCustomers = () => {
    const customers = new Set();
    // Extract from logs
    filteredLogs.forEach(log => {
      const nameObj = userIdToName[log.user_id];
      const email = nameObj && nameObj.userEmail ? nameObj.userEmail : null;
      const customer = email || log.user_id;
      if (customer && !isExcludedUser(customer)) {
        if (selectedRetailer && email && email.includes("@")) {
          const domain = email.split("@")[1].toLowerCase();
          if (domain !== selectedRetailer) return;
        } else if (selectedRetailer && (!email || !email.includes("@"))) {
          return;
        }
        customers.add(customer);
      }
    });
    // Extract from AI SODs
    filteredAiSODs.forEach(ai => {
      const customer = ai.customerEmail || ai.createpersonName;
      if (customer && !isExcludedUser(customer)) {
        if (selectedRetailer && ai.customerEmail && ai.customerEmail.includes("@")) {
          const domain = ai.customerEmail.split("@")[1].toLowerCase();
          if (domain !== selectedRetailer) return;
        } else if (selectedRetailer && (!ai.customerEmail || !ai.customerEmail.includes("@"))) {
          return;
        }
        customers.add(customer);
      }
    });
    return Array.from(customers).sort();
  };

  const allCustomers = extractCustomers();
  const filteredCustomers = allCustomers.filter(customer =>
    customer.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Filtered logs by date, customer, retailer, and ID search
  const dateFilteredLogs = filteredLogs.filter(log => {
    // Date filter
    if (log.datetime && !isWithinDateRange(log.datetime)) return false;
    // Retailer filter
    if (selectedRetailer) {
      const nameObj = userIdToName[log.user_id];
      const email = nameObj && nameObj.userEmail ? nameObj.userEmail : null;
      if (!email || !email.includes("@")) return false;
      const domain = email.split("@")[1].toLowerCase();
      if (domain !== selectedRetailer) return false;
    }
    // Customer filter
    if (selectedCustomer) {
      const nameObj = userIdToName[log.user_id];
      const email = nameObj && nameObj.userEmail ? nameObj.userEmail : null;
      const customer = email || log.user_id;
      if (customer !== selectedCustomer) return false;
    }
    // ID search filter
    if (idSearchTerm) {
      const logId = String(log.id || '');
      if (!logId.toLowerCase().includes(idSearchTerm.toLowerCase())) return false;
    }
    return true;
  });

  // Fetch video data for visible page logs
  const visibleLogIdsKey = dateFilteredLogs.slice(logsPage * LOGS_PER_PAGE, (logsPage + 1) * LOGS_PER_PAGE).map(l => l.id).filter(Boolean).join(',');
  useEffect(() => {
    if (!visibleLogIdsKey) return;
    const ids = visibleLogIdsKey.split(',');
    const idsToFetch = ids.filter(id => !fetchedVideoIds.current.has(id));
    if (idsToFetch.length === 0) return;

    idsToFetch.forEach(id => fetchedVideoIds.current.add(id));

    const fetchVideos = async () => {
      const results = {};
      await Promise.all(idsToFetch.map(async (id) => {
        try {
          const res = await fetch(`${SOD_APIURL}?query=sod_getDetails&id=${id}`);
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0 && data[0].generatedVideo) {
            results[id] = data[0].generatedVideo;
          } else {
            results[id] = null;
          }
        } catch (e) {
          results[id] = null;
        }
      }));
      setLogVideos(prev => ({ ...prev, ...results }));
    };
    fetchVideos();
  }, [visibleLogIdsKey]);

  // Build angle groups: records sharing the same source material (uploadedFileUrls or itemReferences)
  const logAngles = (() => {
    const groups = {};
    dateFilteredLogs.forEach(log => {
      try {
        const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
        let key = null;
        if (fullData.uploadedFileUrls && fullData.uploadedFileUrls.length > 0) {
          key = [...fullData.uploadedFileUrls].sort().join('|');
        } else if (fullData.itemReferences && fullData.itemReferences.length > 0) {
          key = log.user_id + '|' + fullData.itemReferences.map(r => r.itemNumber).sort().join(',');
        }
        if (key) {
          if (!groups[key]) groups[key] = [];
          groups[key].push(log);
        }
      } catch (e) {}
    });
    const angleMap = {};
    Object.values(groups).forEach(group => {
      if (group.length <= 1) return;
      group.forEach(log => {
        angleMap[log.id] = group
          .filter(sibling => sibling.id !== log.id && sibling.generated_image)
          .map(sibling => ({
            id: sibling.id,
            url: `https://genimage.gabrielny.com/halo/uploads/sod/${sibling.generated_image}`
          }));
      });
    });
    return angleMap;
  })();

  // Filtered aiSODs by date, customer, and retailer
  const dateFilteredAiSODs = filteredAiSODs.filter(ai => {
    // Date filter
    if (ai.createDate && !isWithinDateRange(ai.createDate)) return false;
    // Retailer filter
    if (selectedRetailer) {
      const email = ai.customerEmail;
      if (!email || !email.includes("@")) return false;
      const domain = email.split("@")[1].toLowerCase();
      if (domain !== selectedRetailer) return false;
    }
    // Customer filter
    if (selectedCustomer) {
      const customer = ai.customerEmail || ai.createpersonName;
      if (customer !== selectedCustomer) return false;
    }
    return true;
  });

  // --- Recalculate Daily Activity from filteredLogs using datetime field ---
  const dailyActivityMap = {};
  filteredLogs.forEach(log => {
    // Use the datetime field directly from each log entry
    let dateStr = null;
    // Check for datetime field in various possible formats
    if (log.datetime) {
      // Handle different datetime formats
      const date = new Date(log.datetime);
      if (!isNaN(date.getTime())) {
        dateStr = date.toISOString().slice(0, 10); // YYYY-MM-DD
      }
    }
    const isEdit = isEditLog(log);
    if (!dateStr) {
      return;
    }
    if (!dailyActivityMap[dateStr]) {
      dailyActivityMap[dateStr] = { date: dateStr, record_count: 0, edits_count: 0, no_edits_count: 0 };
    }
    dailyActivityMap[dateStr].record_count++;
    if (isEdit) dailyActivityMap[dateStr].edits_count++;
    else dailyActivityMap[dateStr].no_edits_count++;
  });
  const recalculatedDailyData = Object.values(dailyActivityMap).sort((a, b) => a.date.localeCompare(b.date));

  // Filtered dailyData by date
  const dateFilteredDailyData = recalculatedDailyData.filter(d => isWithinDateRange(d.date));

  // Filtered userStats by logs in date range
  const userIdSet = new Set(dateFilteredLogs.map(log => log.user_id));
  const dateFilteredUserStats = filteredUserStats.filter(user => userIdSet.has(user.user_id));

  // Filtered editStats by logs in date range
  // (If you want to recalculate editStats, do it from dateFilteredLogs)
  let editCount = 0;
  let creationCount = 0;
  dateFilteredLogs.forEach(log => {
    if (isEditLog(log)) editCount++;
    else creationCount++;
  });
  const dateFilteredEditStats = [
    { edit: 0, count: editCount },
    { edit: -1, count: creationCount }
  ];

  // Recalculate itemTypeStats from filtered logs
  const itemTypeMap = {};
  dateFilteredLogs.forEach(log => {
    try {
      const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
      const typeName = fullData.typeName || fullData.typename || 'Unknown';
      if (typeName) {
        itemTypeMap[typeName] = (itemTypeMap[typeName] || 0) + 1;
      }
    } catch (e) {
      // Skip logs with invalid data
    }
  });
  
  const dateFilteredItemTypeStats = Object.entries(itemTypeMap).map(([typeName, count]) => ({
    typeName,
    count
  })).sort((a, b) => b.count - a.count);

  // Recalculate stats based on dateFilteredLogs and dateFilteredUserStats
  let dateFilteredStats = stats;
  if (stats && dateFilteredLogs && dateFilteredUserStats) {
    let total_logs = dateFilteredLogs.length;
    let total_edits = 0;
    let total_no_edits = 0;
    let userSet = new Set();
    dateFilteredLogs.forEach(log => {
      userSet.add(log.user_id);
      let isEdit = false;
      try {
        const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
        isEdit = (fullData && typeof fullData === 'object' && fullData.editInformation !== undefined);
      } catch (e) { isEdit = false; }
      if (isEdit) total_edits++;
      else total_no_edits++;
    });
    let unique_users = userSet.size;
    let edit_rate = total_logs > 0 ? total_edits / total_logs : 0;
    let avg_requests_per_user = unique_users > 0 ? total_logs / unique_users : 0;
    dateFilteredStats = {
      ...stats,
      total_logs,
      total_edits,
      unique_users,
      edit_rate,
      total_no_edits,
      avg_requests_per_user
    };
  }

  // --- Recalculate Generations Distributions from filteredLogs ---
  function isEditLog(log) {
    try {
      const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
      const editInfo = fullData && typeof fullData === 'object' ? fullData.editInformation : undefined;
      const isEdit = (typeof editInfo === 'string' && editInfo.trim() !== '' && editInfo.trim().toLowerCase() !== 'unknown');
      console.log('isEditLog:', { user: log.user_id || log.userID || log.id, editInformation: editInfo, isEdit });
      return isEdit;
    } catch (e) {
      console.log('isEditLog: error parsing full_data', { log, error: e });
      return false;
    }
  }

  // --- User Statistics recalculation from filtered logs ---
  const userStatsMap = {};
  dateFilteredLogs.forEach(log => {
    // Use email if available, else user_id
    const userId = (userIdToName[log.user_id]?.userEmail || log.user_id || log.userID || log.id || log.userid || log.email || '').toLowerCase();
    if (!userId) return;
    if (!userStatsMap[userId]) {
      userStatsMap[userId] = {
        userId,
        userEmail: userIdToName[log.user_id]?.userEmail || log.user_id,
        total_requests: 0,
        edits: 0,
        no_edits: 0,
      };
    }
    userStatsMap[userId].total_requests++;
    let isEdit = false;
    try {
      const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
      isEdit = (fullData && typeof fullData === 'object' && fullData.editInformation !== undefined);
    } catch (e) { isEdit = false; }
    if (isEdit) userStatsMap[userId].edits++;
    else userStatsMap[userId].no_edits++;
  });
  const recalculatedUserStats = Object.values(userStatsMap)
      .filter(user => !EMPLOYEE_NAMES.some(emp => user.userEmail && user.userEmail.toLowerCase().includes(emp.toLowerCase())))
      .sort((a, b) => b.total_requests - a.total_requests)
      .slice(0, 10);

  if (loading) {
    return (
        <div className="mt-12 mb-8 flex h-full w-full flex-col gap-5">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading Custom Order Analytics...</p>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="mt-12 mb-8 flex h-full w-full flex-col gap-5 font-dm">
        {/* Filters Row */}
        <div className="flex flex-row justify-between items-center mb-5 mt-2 gap-4">
          {/* Retailer Filter Dropdown */}
          <div className="relative" ref={retailerDropdownRef}>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                Filter by Retailer:
              </label>
              <div className="relative">
                <button
                  onClick={() => setShowRetailerDropdown(!showRetailerDropdown)}
                  className="flex items-center justify-between w-56 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                >
                  <span className={selectedRetailer ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
                    {selectedRetailer || "All Retailers"}
                  </span>
                  {selectedRetailer && (
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedRetailer(null); }}
                      className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Clear filter"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showRetailerDropdown && (
                  <div className="absolute z-50 w-56 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-600">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                      <div className="relative">
                        <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search retailers..."
                          value={retailerSearchTerm}
                          onChange={e => setRetailerSearchTerm(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>
                    {/* Retailer List */}
                    <div className="max-h-60 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedRetailer(null);
                          setShowRetailerDropdown(false);
                          setRetailerSearchTerm("");
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                      >
                        All Retailers
                      </button>
                      {filteredRetailers.map(retailer => (
                        <button
                          key={retailer}
                          onClick={() => {
                            setSelectedRetailer(retailer);
                            setShowRetailerDropdown(false);
                            setRetailerSearchTerm("");
                            setSelectedCustomer(null); // Reset customer filter when retailer changes
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                        >
                          {retailer}
                        </button>
                      ))}
                      {filteredRetailers.length === 0 && retailerSearchTerm && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          No retailers found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Customer Filter Dropdown */}
          <div className="relative" ref={customerDropdownRef}>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                Filter by Customer:
              </label>
              <div className="relative">
                <button
                  onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                  className="flex items-center justify-between w-64 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                >
                  <span className={selectedCustomer ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}>
                    {selectedCustomer || "All Customers"}
                  </span>
                  {selectedCustomer && (
                    <button
                      onClick={e => { e.stopPropagation(); setSelectedCustomer(null); }}
                      className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      title="Clear filter"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showCustomerDropdown && (
                  <div className="absolute z-50 w-64 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg dark:bg-gray-800 dark:border-gray-600">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                      <div className="relative">
                        <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search customers..."
                          value={customerSearchTerm}
                          onChange={e => setCustomerSearchTerm(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        />
                      </div>
                    </div>
                    {/* Customer List */}
                    <div className="max-h-60 overflow-y-auto">
                      <button
                        onClick={() => {
                          setSelectedCustomer(null);
                          setShowCustomerDropdown(false);
                          setCustomerSearchTerm("");
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                      >
                        All Customers
                      </button>
                      {filteredCustomers.map(customer => (
                        <button
                          key={customer}
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowCustomerDropdown(false);
                            setCustomerSearchTerm("");
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                        >
                          {customer}
                        </button>
                      ))}
                      {filteredCustomers.length === 0 && customerSearchTerm && (
                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                          No customers found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Date Range Picker */}
          <DateRangePicker
            clearIcon={null}
            onChange={setDateRange}
            value={dateRange}
          />
        </div>

        {/* Active Filters Indicator */}
        {(selectedRetailer || selectedCustomer || (dateRange && dateRange[0] && dateRange[1]) || idSearchTerm) && (
          <div className="flex items-center space-x-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Active Filters:</span>
            {selectedRetailer && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                Retailer: {selectedRetailer}
                <button
                  onClick={() => setSelectedRetailer(null)}
                  className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  ×
                </button>
              </span>
            )}
            {selectedCustomer && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                Customer: {selectedCustomer}
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="ml-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200"
                >
                  ×
                </button>
              </span>
            )}
            {dateRange && dateRange[0] && dateRange[1] && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200">
                Date Range: {dateRange[0].toLocaleDateString()} - {dateRange[1].toLocaleDateString()}
                <button
                  onClick={() => setDateRange([null, null])}
                  className="ml-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                >
                  ×
                </button>
              </span>
            )}
            {idSearchTerm && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
                ID Search: {idSearchTerm}
                <button
                  onClick={() => setIdSearchTerm("")}
                  className="ml-1 text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-200"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        )}


        {/* Stats Cards */}
        {dateFilteredStats && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4 w-full mb-6">
              <SODStatsCard
                  title="Total Logs"
                  value={dateFilteredStats.total_logs || 0}
                  icon={<KanbanIcon className="h-5 w-5 text-brand-500 dark:text-white" />}

              />
              <SODStatsCard
                  title="Total Edits"
                  value={dateFilteredStats.total_edits || 0}
                  icon={<MarketIcon className="h-6 w-6 text-brand-500 dark:text-white" />}

              />
              <SODStatsCard
                  title="Unique Users"
                  value={dateFilteredStats.unique_users || 0}
                  icon={<ProfileIcon className="h-6 w-6 text-brand-500 dark:text-white" />}

              />
              <SODStatsCard
                  title="Edit Rate"
                  value={dateFilteredStats.edit_rate || 0}
                  icon={<NotificationIcon className="h-6 w-6 text-brand-500 dark:text-white" />}
              />
            </div>
        )}
        {/* Additional Stats Cards */}
        {dateFilteredStats && (
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4 w-full">
              <SODStatsCard
                  title="Creations"
                  value={dateFilteredStats.total_no_edits || 0}
                  icon={<ThemsIcon className="h-5 w-5 text-brand-500 dark:text-white" />}
              />
              <SODStatsCard
                  title="Avg Requests/User"
                  value={dateFilteredStats.avg_requests_per_user || 0}
                  icon={<TablesIcon className="h-6 w-6 text-brand-500 dark:text-white" />}
              />
              <SODStatsCard
                  title="SOD Requests (Submitted)"
                  value={dateFilteredAiSODs.length}
                  icon={<VideoIcon className="h-6 w-6 text-brand-500 dark:text-white" />}
              />
              <SODStatsCard
                  title="Total Item Types"
                  value={dateFilteredStats.total_item_types || 0}
                  icon={<ClockIcon1 className="h-6 w-6 text-brand-500 dark:text-white" />}
              />
            </div>
        )}
        {/* Charts */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <SODActivityChart data={dateFilteredDailyData} title="Daily Activity" />
          <SODDistributionChart data={dateFilteredEditStats} title="Generations Distributions"/>
        </div>
        {/* AI SOD Requests Monthly Chart and Type Distribution */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card extra="p-4">
            <h4 className="text-lg font-bold text-navy-700 dark:text-white mb-4 font-dm">
              AI SOD Requests - Monthly Submissions
            </h4>
            <LineChart
                series={[
                  {
                    name: "Monthly AI SOD Requests",
                    data: (() => {
                      // Group dateFilteredAiSODs by month (YYYY-MM)
                      const monthlyCounts = {};
                      dateFilteredAiSODs.forEach(ai => {
                        if (ai.createDate) {
                          const month = ai.createDate.slice(0, 7); // YYYY-MM
                          monthlyCounts[month] = (monthlyCounts[month] || 0) + 1;
                        }
                      });
                      // Sort months
                      const months = Object.keys(monthlyCounts).sort();
                      return months.map(month => monthlyCounts[month]);
                    })(),
                  },
                ]}
                options={{
                  legend: {
                    position: "top",
                    fontFamily: "DM Sans, sans-serif"
                  },
                  colors: ["#A3CEF1"],
                  chart: {
                    height: 300,
                    type: "line",
                    toolbar: {
                      show: false,
                    },
                    fontFamily: "DM Sans, sans-serif"
                  },
                  xaxis: {
                    categories: (() => {
                      const monthlyCounts = {};
                      dateFilteredAiSODs.forEach(ai => {
                        if (ai.createDate) {
                          const month = ai.createDate.slice(0, 7); // YYYY-MM
                          monthlyCounts[month] = (monthlyCounts[month] || 0) + 1;
                        }
                      });
                      return Object.keys(monthlyCounts).sort();
                    })(),
                    type: "category",
                  },
                  yaxis: {
                    type: "value",
                    title: { text: "Requests" },
                  },
                }}
            />
          </Card>
          <SODTypePieChart data={dateFilteredItemTypeStats} title="Item Type Distribution" />
        </div>
        {/* Approximate Total Cost Button and Card - right aligned under AI SOD Requests */}
        {dateFilteredStats && (
          <div className="flex flex-col items-end mt-6 mb-8 w-full">
            <button
              onClick={() => setShowTotalCost((prev) => !prev)}
              className="px-5 py-2 rounded-lg bg-brand-900 text-white text-base font-semibold shadow hover:bg-brand-800 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300 dark:active:opacity-90 transition duration-200 mb-2"
              style={{ minWidth: '180px' }}
            >
              {showTotalCost ? 'Hide Approximate Total Cost' : 'Show Approximate Total Cost'}
            </button>
            {showTotalCost && (
              <div className="w-full max-w-sm">
                <SODStatsCard
                  title="Approximate Total Cost"
                  value={`$${(dateFilteredStats.total_logs * 0.17).toFixed(2)}`}
                  icon={<DollarIcon className="h-5 w-5 text-brand-500 dark:text-white" />}
                />
              </div>
            )}
          </div>
        )}
        {/* Category Charts */}
        {/* <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <SODItemTypeChart data={itemTypeStats} />
        <SODMetalTypeChart data={metalTypeStats} />
      </div> */}



        {/* User Statistics Chart */}
        <Card extra="p-4 font-dm">
          <h4 className="text-base font-bold font-dm text-navy-700 dark:text-white mb-4">
            User Statistics (Top 10 by Requests)
          </h4>
          {recalculatedUserStats && recalculatedUserStats.length > 0 ? (
              <div className="flex flex-wrap gap-6 items-center justify-center">
                {recalculatedUserStats.map((user, idx) => {
                  const mainLabel = user.userEmail || user.userId;
                  return (
                      <div key={user.userId || idx} className="flex flex-col items-center">
                        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-base font-bold font-dm text-blue-700 dark:text-blue-200 mb-2">
                          {user.total_requests}
                        </div>
                        <div className="text-xs text-gray-700 dark:text-gray-200 font-semibold font-dm mb-1">
                          <div>{mainLabel}</div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-normal font-dm">Edits: {user.edits}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-normal font-dm">Creations: {user.no_edits}</div>
                      </div>
                  );
                })}
              </div>
          ) : (
              <div className="text-center text-xs text-gray-500 dark:text-gray-400 font-dm">No user data for the selected date range.</div>
          )}
        </Card>

        {/* All Logs Table */}
        <Card extra="p-4 font-dm" id="sod-logs-table">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
            <h4 className="text-base font-bold font-dm text-navy-700 dark:text-white">
              All SOD Logs
            </h4>
            {/* ID Search Input */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-navy-700 dark:text-white">
                Search by ID:
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter ID..."
                  value={idSearchTerm}
                  onChange={e => setIdSearchTerm(e.target.value)}
                  className="pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white w-48"
                />
                {idSearchTerm && (
                  <button
                    onClick={() => setIdSearchTerm("")}
                    className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Clear search"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left text-gray-500 dark:text-gray-400 font-dm">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 font-dm">
              <tr>
                <th className="px-2 py-2 font-bold font-dm">ID</th>
                <th className="px-2 py-2 font-bold font-dm">User Email</th>
                <th className="px-2 py-2 font-bold font-dm">Timestamp</th>
                <th className="px-2 py-2 font-bold font-dm">Info</th>
                <th className="px-2 py-2 font-bold font-dm">References</th>
                <th className="px-2 py-2 font-bold font-dm">Results</th>
                <th className="px-2 py-2 font-bold font-dm">Video</th>
                <th className="px-2 py-2 font-bold font-dm">Angles</th>
                <th className="px-2 py-2 font-bold font-dm">Edit/Creation</th>
              </tr>
              </thead>
              <tbody>
              {dateFilteredLogs && dateFilteredLogs.length > 0 ? dateFilteredLogs.slice(logsPage * LOGS_PER_PAGE, (logsPage + 1) * LOGS_PER_PAGE).map((log, idx) => {
                // Helper to truncate text
                const truncate = (text, max = 40) => text && text.length > max ? text.slice(0, max) + '...' : text;
                const cell = (text, max = 40) => (
                    <span className="max-w-xs inline-block truncate text-xs font-normal font-dm" title={text}>{truncate(text, max)}</span>
                );
                
                // Helper to format timestamp
                const formatTimestamp = (datetime) => {
                  if (!datetime) return 'N/A';
                  try {
                    const date = new Date(datetime);
                    if (isNaN(date.getTime())) return 'N/A';
                    // Format as: 2025-09-05 13:58:43 (removing the T and Z, and milliseconds)
                    return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, '');
                  } catch (e) {
                    return 'N/A';
                  }
                };
                
                // Helper to categorize URLs
                const categorizeUrls = (urls) => {
                  const references = [];
                  const results = [];
                  
                  urls.forEach(url => {
                    if (url.includes('/uploads/')) {
                      references.push(url);
                    } else if (url.includes('/assets/')) {
                      results.push(url);
                    }
                  });
                  
                  return { references, results };
                };
                
                // Helper to get generated image URL from full_data
                const getGeneratedImageUrl = (log) => {
                  try {
                    const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
                    if (log.generated_image) {
                      // Construct the URL using the generated_image filename
                      return `https://genimage.gabrielny.com//uploads/sod/${log.generated_image}`;
                    }
                  } catch (e) {
                    console.error('Error parsing full_data for generated image:', e);
                  }
                  return null;
                };
                
                // Determine Edit or Creation
                let isEdit = false;
                try {
                  const fullData = typeof log.full_data === 'string' ? JSON.parse(log.full_data) : log.full_data || {};
                  isEdit = (fullData && typeof fullData === 'object' && fullData.editInformation !== undefined);
                } catch (e) { isEdit = false; }
                
                const nameObj = userIdToName[log.user_id];
                const email = nameObj && nameObj.userEmail ? nameObj.userEmail : null;
                const display = email || log.user_id;
                
                // Process references_url and generated_image
                let referenceUrls = [];
                let resultUrls = [];
                
                // Always put references_url in references column
                if (log.references_url) {
                  const urls = String(log.references_url).split(/\s+/).filter(Boolean);
                  referenceUrls = urls; // All URLs from references_url go to references
                }
                
                // Add generated image to results if it exists
                const generatedImageUrl = getGeneratedImageUrl(log);
                if (generatedImageUrl) {
                  resultUrls.push(generatedImageUrl);
                }
                
                return (
                    <tr key={log.id || idx} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 font-dm">
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        <button
                            className="text-blue-600 underline hover:text-blue-800 text-xs font-normal font-dm"
                            onClick={() => setLogDetailsPopup(log)}
                        >
                          {log.id || 'N/A'}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{cell(display, 30)}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{formatTimestamp(log.datetime)}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{cell(log.info, 40)}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        {referenceUrls.length > 0 ? referenceUrls.map((url, i) => (
                            <button
                                key={i}
                                className="text-blue-600 underline block text-xs font-normal font-dm"
                                onClick={() => setReferencePopupUrl(url)}
                                title={url}
                            >
                              {url.length > 30 ? url.substring(0, 30) + '...' : url}
                            </button>
                        )) : (
                            <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        {resultUrls.length > 0 ? resultUrls.map((url, i) => (
                            <button
                                key={i}
                                className="text-blue-600 underline block text-xs font-normal font-dm"
                                onClick={() => setReferencePopupUrl(url)}
                                title={url}
                            >
                              {url.length > 30 ? url.substring(0, 30) + '...' : url}
                            </button>
                        )) : (
                            <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        {(() => {
                          const videoFile = logVideos[log.id];
                          if (videoFile === undefined) return <span className="text-gray-300">...</span>;
                          if (!videoFile) return <span className="text-gray-400">N/A</span>;
                          const videoUrl = `https://genimage.gabrielny.com/halo/video/${videoFile}.mp4`;
                          return (
                            <button
                              className="text-blue-600 underline block text-xs font-normal font-dm"
                              onClick={() => setReferencePopupUrl(videoUrl)}
                              title={videoUrl}
                            >
                              {videoUrl.length > 30 ? videoUrl.substring(0, 30) + '...' : videoUrl}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        {(() => {
                          const angles = logAngles[log.id];
                          if (!angles || angles.length === 0) return <span className="text-gray-400">N/A</span>;
                          return (
                            <div className="flex flex-wrap gap-1">
                              {angles.map((angle, i) => (
                                <button
                                  key={angle.id}
                                  className="text-blue-600 underline text-xs font-normal font-dm"
                                  onClick={() => setReferencePopupUrl(angle.url)}
                                  title={angle.url}
                                >
                                  [{i + 1}]
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        {isEdit ? 'Edit' : 'Creation'}
                      </td>
                    </tr>
                );
              }) : (
                  <tr><td colSpan={9} className="text-center py-4 text-xs font-dm">No logs found</td></tr>
              )}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {dateFilteredLogs && dateFilteredLogs.length > LOGS_PER_PAGE && (
                <div className="flex justify-center items-center gap-4 mt-4 font-dm">
                  <button
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50 text-xs font-normal font-dm"
                      onClick={() => setLogsPage(p => Math.max(0, p - 1))}
                      disabled={logsPage === 0}
                  >
                    Previous
                  </button>
                  <span className="text-xs font-dm">Page {logsPage + 1} of {Math.ceil(dateFilteredLogs.length / LOGS_PER_PAGE)}</span>
                  <button
                      className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded disabled:opacity-50 text-xs font-normal font-dm"
                      onClick={() => setLogsPage(p => Math.min(Math.ceil(dateFilteredLogs.length / LOGS_PER_PAGE) - 1, p + 1))}
                      disabled={logsPage >= Math.ceil(dateFilteredLogs.length / LOGS_PER_PAGE) - 1}
                  >
                    Next
                  </button>
                </div>
            )}
          </div>
        </Card>

        {/* AI SOD Requests Table */}
        <Card extra="p-4 font-dm">
          <h4 className="text-base font-bold font-dm text-navy-700 dark:text-white mb-4">
            AI SOD Requests (AI Image Requests)
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left text-gray-500 dark:text-gray-400 font-dm">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400 font-dm">
              <tr>
                <th className="px-2 py-2 font-bold font-dm">Design No</th>
                <th className="px-2 py-2 font-bold font-dm">Created By</th>
                <th className="px-2 py-2 font-bold font-dm">Created Date</th>
                <th className="px-2 py-2 font-bold font-dm">Customer PO</th>
                <th className="px-2 py-2 font-bold font-dm">Special Instructions</th>
                <th className="px-2 py-2 font-bold font-dm">Reference Files</th>
                <th className="px-2 py-2 font-bold font-dm">Status</th>
              </tr>
              </thead>
              <tbody>
              {dateFilteredAiSODs && dateFilteredAiSODs.length > 0 ? dateFilteredAiSODs.map((ai, idx) => {
                const truncate = (text, max = 40) => text && text.length > max ? text.slice(0, max) + '...' : text;
                // Extract reference file URLs from specialinstructions
                let refFiles = [];
                if (ai.specialinstructions) {
                  const urlRegex = /(https?:\/\/[^\s,]+)/g;
                  refFiles = ai.specialinstructions.match(urlRegex) || [];
                }
                return (
                    <tr key={ai.id || idx} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 font-dm">
                      <td className="px-2 py-2 text-xs font-normal font-dm">{ai.designNo}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{ai.createpersonName || ai.customerEmail || '-'}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{ai.createDate ? ai.createDate.slice(0, 19).replace('T', ' ') : '-'}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{truncate(ai.customerPo, 20)}</td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        <button className="text-blue-600 underline" onClick={() => setAiSODDetails(ai)}>{truncate(ai.specialinstructions, 40)}</button>
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">
                        {refFiles.length > 0 ? refFiles.map((url, i) => (
                            <button 
                              key={i} 
                              className="text-blue-600 underline block text-xs font-normal font-dm" 
                              onClick={() => setReferencePopupUrl(url)}
                              title={url}
                            >
                              {url.length > 30 ? url.substring(0, 30) + '...' : url}
                            </button>
                        )) : <span className="text-gray-400">N/A</span>}
                      </td>
                      <td className="px-2 py-2 text-xs font-normal font-dm">{ai.status}</td>
                    </tr>
                );
              }) : (
                  <tr><td colSpan={7} className="text-center py-4 text-xs font-dm">No AI SOD requests found</td></tr>
              )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* AI SOD Details Modal */}
        {aiSODDetails && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 font-dm">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 max-w-3xl w-full relative font-dm max-h-[80vh] overflow-y-auto">
                <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl font-dm" onClick={() => setAiSODDetails(null)}>&times;</button>
                <h4 className="text-base font-bold mb-4 text-navy-700 dark:text-white font-dm">AI SOD Request Details</h4>
                <div className="space-y-2 text-xs font-dm">
                  {Object.entries(aiSODDetails).map(([key, value]) => (
                      <div key={key}><span className="font-semibold">{key}:</span> {typeof value === 'string' ? value : JSON.stringify(value)}</div>
                  ))}
                </div>
              </div>
            </div>
        )}

        {/* Log Details Modal/Section */}
        {referencePopupUrl && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[100] font-dm">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-4 max-w-2xl w-full relative font-dm">
                <button 
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl font-dm" 
                  onClick={() => {
                    setReferencePopupUrl(null);
                    if (viewingUrlFromLogDetails) {
                      setViewingUrlFromLogDetails(false);
                      // Don't close logDetailsPopup, just return to it
                    }
                  }}
                >
                  &times;
                </button>
                {viewingUrlFromLogDetails && (
                  <button 
                    className="absolute top-2 left-2 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 font-dm" 
                    onClick={() => {
                      setReferencePopupUrl(null);
                      setViewingUrlFromLogDetails(false);
                    }}
                  >
                    ← Back to Log Details
                  </button>
                )}
                <h4 className="text-base font-bold mb-4 text-navy-700 dark:text-white font-dm">Reference Link</h4>
                <div className="w-full h-[60vh] flex items-center justify-center gap-4 flex-wrap">
                  {(() => {
                    const url = referencePopupUrl;
                    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);
                    const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(url);
                    if (isImage) {
                      return (
                          <img
                              src={url}
                              alt="Reference"
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }}
                          />
                      );
                    } else if (isVideo) {
                      return (
                          <video
                              src={url}
                              controls
                              autoPlay
                              style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', margin: '0 auto' }}
                          />
                      );
                    } else {
                      return (
                          <iframe src={url} title="Reference" className="w-full h-full rounded border" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      );
                    }
                  })()}
                </div>
              </div>
            </div>
        )}
        {logDetailsPopup && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 font-dm">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 max-w-6xl w-full relative font-dm max-h-[90vh] overflow-y-auto">
                <button 
                  className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 dark:hover:text-white text-xl font-dm" 
                  onClick={() => {
                    setLogDetailsPopup(null);
                    setViewingUrlFromLogDetails(false);
                  }}
                >
                  &times;
                </button>
                <h4 className="text-base font-bold mb-4 text-navy-700 dark:text-white font-dm">Log Details for User ID: {logDetailsPopup.user_id}</h4>
                {(() => {
                  let fullData = {};
                  try { fullData = typeof logDetailsPopup.full_data === 'string' ? JSON.parse(logDetailsPopup.full_data) : logDetailsPopup.full_data || {}; } catch (e) { fullData = {}; }
                  
                  // Helper to format value display
                  const formatValue = (value) => {
                    if (value === null || value === undefined) return 'N/A';
                    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
                    if (typeof value === 'object') return JSON.stringify(value);
                    return String(value);
                  };
                  
                  // Helper to render URL as clickable link
                  const renderUrl = (url, label = 'View') => {
                    if (!url) return 'N/A';
                    return (
                      <button 
                        className="text-blue-600 underline hover:text-blue-800 break-all text-left w-full" 
                        onClick={() => { 
                          setReferencePopupUrl(url); 
                          setViewingUrlFromLogDetails(true);
                        }}
                        title={url}
                      >
                        {url}
                      </button>
                    );
                  };

                  // Helper to extract Requested Modifications from a prompt
                  const extractRequestedModifications = (text) => {
                    if (!text) return 'N/A';
                    try {
                      const s = String(text);
                      const startMatch = s.match(/Requested\s+Modifications\s*:\s*/i);
                      if (!startMatch || startMatch.index === undefined) return 'N/A';
                      const startIdx = startMatch.index + startMatch[0].length;
                      const remainder = s.slice(startIdx);
                      const endMatch = remainder.match(/General\s+Characteristics\s*&\s*Constraints\s*:/i);
                      const endIdx = endMatch && endMatch.index !== undefined ? startIdx + endMatch.index : s.length;
                      const result = s.slice(startIdx, endIdx).trim();
                      return result || 'N/A';
                    } catch (e) {
                      return 'N/A';
                    }
                  };

                  const requestedMods = extractRequestedModifications(logDetailsPopup.generated_prompt || fullData.generatingPrompt);
                  
                  return (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-xs font-dm">
                        {/* Basic Information */}
                        <div className="space-y-3">
                          <h5 className="font-bold text-navy-700 dark:text-white border-b pb-2">Basic Information</h5>
                          <div><span className="font-semibold">ID:</span> {logDetailsPopup.id}</div>
                          <div><span className="font-semibold">User ID:</span> {logDetailsPopup.user_id}</div>
                          <div><span className="font-semibold">Info:</span> {logDetailsPopup.info}</div>
                          <div><span className="font-semibold">Edit/Creation:</span> {(fullData.editInformation !== undefined) ? 'Edit' : 'Creation'}</div>
                          <div><span className="font-semibold">DateTime:</span> {logDetailsPopup.datetime}</div>
                          <div><span className="font-semibold">Updated At:</span> {logDetailsPopup.updated_at || 'N/A'}</div>
                        </div>
                        
                        {/* Generated Content */}
                        <div className="space-y-3">
                          <h5 className="font-bold text-navy-700 dark:text-white border-b pb-2">Generated Content</h5>
                          <div><span className="font-semibold">Generated Image:</span> {logDetailsPopup.generated_image ? renderUrl(`https://genimage.gabrielny.com//uploads/sod/${logDetailsPopup.generated_image}`, 'View Generated Image') : 'N/A'}</div>
                          <div><span className="font-semibold">Generated Video:</span> {(() => {
                            const videoFile = logVideos[logDetailsPopup.id];
                            if (videoFile === undefined) return <span className="text-gray-400">Loading...</span>;
                            if (!videoFile) return 'N/A';
                            const videoUrl = `https://genimage.gabrielny.com/halo/video/${videoFile}.mp4`;
                            return renderUrl(videoUrl, 'View Generated Video');
                          })()}</div>
                          <div><span className="font-semibold">References URL:</span> 
                            {(() => {
                              if (!logDetailsPopup.references_url) return 'N/A';
                              const urls = String(logDetailsPopup.references_url).split(/\s+/).filter(Boolean);
                              if (urls.length === 1) {
                                return renderUrl(urls[0], 'View Reference');
                              } else if (urls.length > 1) {
                                return (
                                  <div className="space-y-2">
                                    {urls.map((url, i) => (
                                      <div key={i} className="border-l-2 border-gray-200 pl-2">
                                        <span className="text-xs text-gray-500 font-semibold">Reference {i + 1}:</span>
                                        <div className="mt-1">
                                          {renderUrl(url, `Reference ${i + 1}`)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              return 'N/A';
                            })()}
                          </div>
                          <div className="max-h-32 overflow-y-auto">
                            <span className="font-semibold">Generated Prompt:</span>
                            <div className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                              {logDetailsPopup.generated_prompt || 'N/A'}
                            </div>
                          </div>
                        </div>
                        
                        {/* Full Data Details */}
                        <div className="space-y-3 lg:col-span-2">
                          <h5 className="font-bold text-navy-700 dark:text-white border-b pb-2">Full Data Details</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div><span className="font-semibold">Type:</span> {formatValue(fullData.type)}</div>
                            <div><span className="font-semibold">Type Name:</span> {formatValue(fullData.typeName)}</div>
                            <div><span className="font-semibold">Metal Type:</span> {formatValue(fullData.metalType)}</div>
                            <div><span className="font-semibold">Metal Type Name:</span> {formatValue(fullData.metalTypeName)}</div>
                            <div><span className="font-semibold">Center Stone Shape:</span> {formatValue(fullData.centerStoneShape)}</div>
                            <div><span className="font-semibold">Center Stone Shape Name:</span> {formatValue(fullData.centerStoneShapeName)}</div>
                            <div><span className="font-semibold">Generating Version:</span> {formatValue(fullData.generatingVersion)}</div>
                            <div><span className="font-semibold">Generating Prompt:</span> {formatValue(fullData.generatingPrompt)}</div>
                            <div><span className="font-semibold">Edit Information:</span> {formatValue(fullData.editInformation)}</div>
                            <div className="col-start-1 md:col-start-2 lg:col-start-3"><span className="font-semibold">Requested Modifications:</span> {requestedMods}</div>
                            <div><span className="font-semibold">Edit Information Error:</span> {formatValue(fullData.editInformationError)}</div>
                            <div><span className="font-semibold">Category Locked:</span> {formatValue(fullData.categoryLocked)}</div>
                          </div>
                        </div>
                        
                        {/* Raw JSON Data */}
                        <div className="lg:col-span-2">
                          <h5 className="font-bold text-navy-700 dark:text-white border-b pb-2 mb-3">Raw JSON Data</h5>
                          <div className="relative">
                            <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto font-dm text-xs max-h-64 overflow-y-auto">
                              {JSON.stringify(logDetailsPopup, null, 2)}
                            </pre>
                            <button
                                className="absolute top-2 right-2 px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                                onClick={() => {
                                  navigator.clipboard.writeText(JSON.stringify(logDetailsPopup, null, 2));
                                }}
                                title="Copy JSON to clipboard"
                            >
                              Copy All
                            </button>
                          </div>
                        </div>
                      </div>
                  );
                })()}
              </div>
            </div>
        )}
      </div>
  );
};

export default SODDashboard; 